/**
 * RequireRole component
 * Protects routes by requiring specific user roles
 * Redirects unauthorized users to the home page
 */
import { Navigate } from 'react-router-dom'
import { useCurrentUser } from '@/hooks/useCurrentUser'

export interface RequireRoleProps {
  /** Array of roles that are allowed to access this content */
  roles: string[]
  /** Content to render if user has required role */
  children: React.ReactNode
}

/**
 * Renders children only if the current user has one of the specified roles
 * Redirects to home page if user lacks required role
 * 
 * @example
 * ```tsx
 * // Protect admin-only page
 * <RequireRole roles={['super_admin']}>
 *   <UserManagementPage />
 * </RequireRole>
 * 
 * // Allow multiple roles
 * <RequireRole roles={['super_admin', 'developer']}>
 *   <ConfigPage />
 * </RequireRole>
 * ```
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { data: user, isLoading } = useCurrentUser()

  // Don't render anything while loading to prevent flash
  if (isLoading) {
    return null
  }

  // Redirect if user not loaded or role not in allowed list
  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
